import React, { useState, useEffect, useMemo } from 'react';
import { 
  AlertTriangle, Construction, Siren, Info, ArrowRight, 
  Activity, Clock, MapPin, ChevronLeft, ChevronRight, Filter, Search 
} from 'lucide-react';
import './EventLog.css';

const EventLog = () => {
  // 1. Mock Data มาตรฐาน (12 Cases)
  const initialMockEvents = [
    { id: 101, type: 'Accident', source: 'OBU', detail: 'Collision detected at KM 15.5', status: 'Active', time: '10:42 AM' },
    { id: 102, type: 'Road Maintenance', source: 'Central', detail: 'Left lane closed for repair', status: 'Active', time: '10:30 AM' },
    { id: 103, type: 'Emergency Response', source: 'OBU', detail: 'Ambulance approaching fast', status: 'Active', time: '10:15 AM' },
    { id: 104, type: 'Traffic Congestion', source: 'Central', detail: 'Heavy traffic flow detected', status: 'Warning', time: '09:55 AM' },
    { id: 105, type: 'Reduce Speed', source: 'Central', detail: 'Speed limit lowered to 60 km/h', status: 'Active', time: '09:40 AM' },
    { id: 106, type: 'Queue Backup', source: 'Central', detail: 'Queue building up at Exit 4', status: 'Warning', time: '09:30 AM' },
    { id: 107, type: 'Lane Change', source: 'Central', detail: 'Merge right ahead', status: 'Info', time: '09:15 AM' },
    { id: 108, type: 'Advice to use off-ramp', source: 'Central', detail: 'Use Ramp B to avoid traffic', status: 'Info', time: '09:00 AM' },
    { id: 109, type: 'Rest Area Ahead', source: 'Central', detail: 'Rest stop 2km ahead', status: 'Info', time: '08:45 AM' },
    { id: 110, type: 'Test RSU Vertical', source: 'RSU', detail: 'Vertical sensor calibration test', status: 'Test', time: '08:30 AM' },
    { id: 111, type: 'Test RSU Horizontal', source: 'RSU', detail: 'Horizontal beam signal check', status: 'Test', time: '08:15 AM' },
    { id: 112, type: 'Accident', source: 'Central', detail: 'Cleared accident, debris on shoulder', status: 'Resolved', time: '07:50 AM' },
  ];

  const [allEvents, setAllEvents] = useState(initialMockEvents);
  
  // --- Filter States ---
  const [filterType, setFilterType] = useState('All');
  const [filterSource, setFilterSource] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // --- Pagination States ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 2. ระบบ Sync ข้อมูลจากหน้าแผนที่ (MAP_EVENTS)
  useEffect(() => {
    const syncWithMap = () => {
      const savedMapEvents = localStorage.getItem('MAP_EVENTS');
      let combined = [...initialMockEvents];
      
      if (savedMapEvents) {
        const parsedMapEvents = JSON.parse(savedMapEvents);
        const formattedMapEvents = parsedMapEvents.map(ev => ({
          id: ev.id,
          type: 'Manual Report', 
          source: 'MapUI', 
          detail: `${ev.detail} (RSU: ${ev.rsuName || 'N/A'})`,
          status: 'Active',
          time: ev.time,
          isManual: true 
        }));
        combined = [...formattedMapEvents, ...initialMockEvents];
      }
      setAllEvents(combined);
    };

    syncWithMap();
    window.addEventListener('storage', syncWithMap);
    return () => window.removeEventListener('storage', syncWithMap);
  }, []);

  // --- Filter Logic ---
  const filteredEvents = useMemo(() => {
    return allEvents.filter(ev => {
      const matchType = filterType === 'All' || ev.type === filterType || (filterType === 'Manual' && ev.isManual);
      const matchSource = filterSource === 'All' || ev.source === filterSource;
      const matchSearch = ev.detail.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          ev.type.toLowerCase().includes(searchTerm.toLowerCase());
      return matchType && matchSource && matchSearch;
    });
  }, [allEvents, filterType, filterSource, searchTerm]);

  // เมื่อ Filter เปลี่ยน ให้กลับไปหน้า 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterSource, searchTerm]);

  // --- Pagination Logic ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEvents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  // Helper Functions
  const getEventIcon = (type, isManual) => {
    if (isManual) return <MapPin color="#ef4444" />;
    if (type.includes('Accident')) return <AlertTriangle color="#ef4444" />;
    if (type.includes('Maintenance')) return <Construction color="#f59e0b" />;
    if (type.includes('Emergency')) return <Siren color="#ef4444" />;
    if (type.includes('Test')) return <Activity color="#3b82f6" />;
    if (type.includes('Lane') || type.includes('ramp')) return <ArrowRight color="#10b981" />;
    return <Info color="#6b7280" />;
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'Active': return 'status-active';
      case 'Warning': return 'status-warning';
      case 'Resolved': return 'status-resolved';
      case 'Test': return 'status-test';
      default: return 'status-info';
    }
  };

  return (
    <div className="event-log-container">
      <div className="event-header">
        <div>
          <h2>Event Logs</h2>
          <p className="subtitle">Real-time Traffic & System Activity Tracking</p>
        </div>
        <div className="event-stats">
          <span className="badge-total">{filteredEvents.length} Results Found</span>
        </div>
      </div>

      {/* --- Filter Section --- */}
      <div className="filter-bar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            placeholder="Search detail or type..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <div className="filter-item">
            <Filter size={16} />
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="All">All Types</option>
              <option value="Manual">Manual Report</option>
              <option value="Accident">Accident</option>
              <option value="Road Maintenance">Maintenance</option>
              <option value="Emergency Response">Emergency</option>
              <option value="Test RSU Vertical">System Test</option>
            </select>
          </div>

          <div className="filter-item">
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
              <option value="All">All Sources</option>
              <option value="OBU">OBU</option>
              <option value="Central">Central</option>
              <option value="RSU">RSU</option>
              <option value="MapUI">MapUI</option>
            </select>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="event-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Source</th>
              <th>Detail</th>
              <th>Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((ev) => (
              <tr key={ev.id} className={ev.isManual ? 'row-manual-highlight' : ''}>
                <td className="col-id">
                  {typeof ev.id === 'string' ? ev.id.substring(0, 8) : `#${ev.id}`}
                </td>
                <td className="col-type">
                  <div className="type-wrapper">
                    {getEventIcon(ev.type, ev.isManual)}
                    <span>{ev.type}</span>
                  </div>
                </td>
                <td className="col-source">
                  <span className={`source-tag source-${ev.source.toLowerCase()}`}>
                    {ev.source}
                  </span>
                </td>
                <td className="col-detail">
                  {ev.detail}
                  {ev.isManual && <span className="live-label">LIVE</span>}
                </td>
                <td className="col-time">
                  <Clock size={14} style={{marginRight: '5px'}}/>
                  {ev.time}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(ev.status)}`}>
                    {ev.status}
                  </span>
                </td>
              </tr>
            ))}
            {currentItems.length === 0 && (
              <tr>
                <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#94a3b8'}}>
                  No events found matching your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination UI --- */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredEvents.length)} of {filteredEvents.length}
          </div>
          <div className="pagination-buttons">
            <button className="btn-page" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
              <ChevronLeft size={18} />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button key={i} onClick={() => paginate(i + 1)} className={`btn-page ${currentPage === i + 1 ? 'active' : ''}`}>
                {i + 1}
              </button>
            ))}
            <button className="btn-page" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventLog;